import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import { hotArticles, paginate, searchArticles, totalPages } from "@/utils/news";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";

const PAGE_SIZE = 5;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [page, setPage] = useState(1);

  usePageMeta({
    title: keyword ? `“${keyword}”的搜索结果 - 51吃瓜网站` : "搜索资讯 - 51吃瓜网站",
    description: "在51吃瓜网站搜索热点新闻、娱乐八卦与明星动态，快速找到你关心的吃瓜资讯。",
    keywords: "51吃瓜网站,搜索,热点新闻,娱乐八卦",
  });

  useEffect(() => {
    setInput(keyword);
    setPage(1);
  }, [keyword]);

  const results = useMemo(() => searchArticles(keyword), [keyword]);
  const total = totalPages(results.length, PAGE_SIZE);
  const current = Math.min(page, total);
  const pageItems = paginate(results, current, PAGE_SIZE);
  const recommend = hotArticles(5);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = input.trim();
    setSearchParams(kw ? { q: kw } : {});
  };

  return (
    <div className="bg-background-50">
      <div className="mx-auto w-full max-w-[1200px] px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: "搜索" }]} />

        <div className="mt-5 rounded-lg border border-background-200 bg-secondary-50 p-5 md:p-6">
          <h1 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
            搜索站内资讯
          </h1>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-2 sm:flex-row">
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2.5 focus-within:border-accent-400">
              <i className="ri-search-line text-lg text-foreground-400"></i>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入关键词，如：明星动态、新剧、夜市"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>
          <p className="mt-3 text-xs text-foreground-400">
            当前为演示功能，搜索结果基于站内演示数据，暂不完全。
          </p>
        </div>

        <div className="mt-6">
          {keyword && (
            <p className="text-sm text-foreground-600">
              关键词
              <span className="mx-1 font-bold text-primary-600">“{keyword}”</span>
              共找到
              <span className="mx-1 font-bold text-primary-600">{results.length}</span>
              条相关资讯
            </p>
          )}
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="mt-5 flex flex-col gap-4">
              {pageItems.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
            <Pagination page={current} total={total} onChange={setPage} />
          </>
        ) : (
          <div className="mt-6">
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-14 text-center">
              <p className="text-4xl">🔍</p>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                {keyword ? "没有找到相关资讯" : "输入关键词开始搜索"}
              </h3>
              <p className="mt-2 text-sm text-foreground-500">
                {keyword
                  ? "换个关键词试试，或者看看下面这些热门内容。"
                  : "试试搜索“明星动态”“新剧”“网络热议”等关键词。"}
              </p>
            </div>

            <section className="mt-10">
              <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <i className="ri-fire-line text-primary-500"></i>
                热门推荐
              </h2>
              <div className="flex flex-col gap-4">
                {recommend.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
              <div className="mt-6 text-center">
                <Link
                  to="/"
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-home-4-line"></i> 返回首页
                </Link>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}